export type CachedCreatorOption = {
  id: string;
  value: string;
  department_id?: string;
  user_id?: string | null;
  legacy?: boolean;
};

export type CreatorOptionsCacheCriteria = {
  loginId: string;
  startDate: string;
  endDate: string;
  departmentIds: string[];
  teamIds: string[];
  includeTemporary: boolean;
  userDep: string;
  userTeamId: string;
};

export type CreatorOptionsCacheRecord = {
  key: string;
  version: 1 | 2 | 3;
  savedAt: number;
  lastAccessedAt?: number;
  expiresAt?: number;
  criteria?: CreatorOptionsCacheCriteria;
  cursor: number | null;
  options: CachedCreatorOption[];
  nextPageCursor?: string | null;
  complete?: boolean;
  // 取得を1,500件で止めていた旧版キャッシュの判別専用。
  limited?: boolean;
};

const DB_NAME = 'weekly-report-browser-cache';
// v4: 週報の新規作成が差分同期ログへ記録されていなかった期間に保存された
// 発行者候補(作成者が欠けたまま残る)を、オブジェクトストアごと破棄するための版数。
const DB_VERSION = 4;
const STORE_NAME = 'creator-options';
const MAX_CACHE_RECORDS = 24;
const MAX_CACHED_OPTIONS = 100_000;
export const CREATOR_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const CREATOR_PREFETCH_FRESH_MS = 24 * 60 * 60 * 1000;
export const CREATOR_SYNC_MIN_REVALIDATE_MS = 60 * 1000;
export const CREATOR_SYNC_BACKGROUND_INTERVAL_MS = 5 * 60 * 1000;
export const CREATOR_INITIAL_PAGE_SIZE = 100;
export const CREATOR_BACKGROUND_PAGE_SIZE = 1000;
const CREATOR_CACHE_CHANNEL = 'weekly-report-creator-cache-v3';
const CREATOR_CACHE_SENDER_ID = `${Date.now()}:${Math.random()}`;

type CreatorCacheBroadcastMessage = {
  type: 'updated' | 'reset';
  key: string;
  senderId?: string;
};

const normalizeCriteriaValue = (value: unknown): string =>
  String(value ?? '').trim().toLocaleLowerCase();

const normalizeCriteriaIds = (values: string[]): string[] =>
  Array.from(
    new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean))
  ).sort();

export const createCreatorOptionsCacheCriteria = (args: {
  loginId: string | null | undefined;
  startDate: string;
  endDate: string;
  departmentIds: string[];
  teamIds: string[];
  includeTemporary?: boolean;
  userDep?: string;
  userTeamId?: string | number;
}): CreatorOptionsCacheCriteria => ({
  loginId: normalizeCriteriaValue(args.loginId || 'unknown'),
  startDate: String(args.startDate ?? '').trim(),
  endDate: String(args.endDate ?? '').trim(),
  departmentIds: normalizeCriteriaIds(args.departmentIds),
  teamIds: normalizeCriteriaIds(args.teamIds),
  includeTemporary: Boolean(args.includeTemporary),
  userDep: normalizeCriteriaValue(args.userDep),
  userTeamId: normalizeCriteriaValue(args.userTeamId),
});

export const areCreatorOptionsCacheCriteriaEqual = (
  left: CreatorOptionsCacheCriteria | undefined,
  right: CreatorOptionsCacheCriteria | undefined
): boolean =>
  Boolean(left) &&
  Boolean(right) &&
  left?.loginId === right?.loginId &&
  left?.startDate === right?.startDate &&
  left?.endDate === right?.endDate &&
  left?.includeTemporary === right?.includeTemporary &&
  left?.userDep === right?.userDep &&
  left?.userTeamId === right?.userTeamId &&
  left?.departmentIds?.join(',') === right?.departmentIds?.join(',') &&
  left?.teamIds?.join(',') === right?.teamIds?.join(',');

const broadcastCreatorCacheMessage = (
  message: CreatorCacheBroadcastMessage
): void => {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CREATOR_CACHE_CHANNEL);
    channel.postMessage({ ...message, senderId: CREATOR_CACHE_SENDER_ID });
    channel.close();
  } catch {
    // BroadcastChannelが使えない環境でもIndexedDBキャッシュは継続する。
  }
};

const openDatabase = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      // v3以前の候補は公開設定変更・週報の新規作成を完全には検知できないため引き継がない。
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex('lastAccessedAt', 'lastAccessedAt');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve(null);
  });
};

const finishTransaction = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });

export const buildCreatorOptionsIndexedDbKey = (
  loginId: string | null | undefined,
  criteriaKey: string
): string =>
  `creator-options:v3:${String(loginId ?? 'unknown').trim().toLowerCase()}:${criteriaKey}`;

export const isCreatorOptionsCacheExpired = (
  record: CreatorOptionsCacheRecord,
  now = Date.now()
): boolean =>
  record.version !== 3 ||
  Number(record.expiresAt ?? record.savedAt + CREATOR_CACHE_TTL_MS) <= now;

export const selectCreatorOptionCacheKeysToPrune = (
  records: CreatorOptionsCacheRecord[],
  now = Date.now()
): string[] => {
  const obsoleteKeys: string[] = [];
  let retainedRecords = 0;
  let retainedOptions = 0;
  records
    .slice()
    .sort(
      (a, b) =>
        Number(b.lastAccessedAt ?? b.savedAt ?? 0) -
        Number(a.lastAccessedAt ?? a.savedAt ?? 0)
    )
    .forEach((item) => {
      const optionCount = Array.isArray(item.options) ? item.options.length : 0;
      const canRetain =
        item.version === 3 &&
        !isCreatorOptionsCacheExpired(item, now) &&
        retainedRecords < MAX_CACHE_RECORDS &&
        retainedOptions + optionCount <= MAX_CACHED_OPTIONS;
      if (!canRetain) {
        obsoleteKeys.push(item.key);
        return;
      }
      retainedRecords += 1;
      retainedOptions += optionCount;
    });
  return obsoleteKeys;
};

export async function readCreatorOptionsCache(
  key: string
): Promise<CreatorOptionsCacheRecord | null> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDatabase();
    if (!db) return null;
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);
    const value = await new Promise<any>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    if (
      !value ||
      value.version !== 3 ||
      !Array.isArray(value.options)
    ) {
      await finishTransaction(transaction);
      return null;
    }
    if (isCreatorOptionsCacheExpired(value)) {
      store.delete(key);
      await finishTransaction(transaction);
      return null;
    }
    const accessedRecord: CreatorOptionsCacheRecord = {
      ...value,
      lastAccessedAt: Date.now(),
    };
    store.put(accessedRecord);
    await finishTransaction(transaction);
    return accessedRecord;
  } catch {
    return null;
  } finally {
    db?.close();
  }
}

export async function writeCreatorOptionsCache(
  record: CreatorOptionsCacheRecord
): Promise<boolean> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDatabase();
    if (!db) return false;

    const now = Date.now();
    const persistedRecord: CreatorOptionsCacheRecord = {
      ...record,
      version: 3,
      lastAccessedAt: record.lastAccessedAt ?? now,
      expiresAt: now + CREATOR_CACHE_TTL_MS,
    };
    const writeTransaction = db.transaction(STORE_NAME, 'readwrite');
    writeTransaction.objectStore(STORE_NAME).put(persistedRecord);
    await finishTransaction(writeTransaction);

    const readTransaction = db.transaction(STORE_NAME, 'readonly');
    const getAllRequest = readTransaction.objectStore(STORE_NAME).getAll();
    const allRecords = await new Promise<CreatorOptionsCacheRecord[]>((resolve, reject) => {
      getAllRequest.onsuccess = () => resolve(getAllRequest.result ?? []);
      getAllRequest.onerror = () => reject(getAllRequest.error);
    });

    const obsoleteKeys = selectCreatorOptionCacheKeysToPrune(allRecords, now);
    if (obsoleteKeys.length > 0) {
      const pruneTransaction = db.transaction(STORE_NAME, 'readwrite');
      const store = pruneTransaction.objectStore(STORE_NAME);
      obsoleteKeys.forEach((obsoleteKey) => store.delete(obsoleteKey));
      await finishTransaction(pruneTransaction);
    }
    broadcastCreatorCacheMessage({ type: 'updated', key: record.key });
    return true;
  } catch {
    return false;
  } finally {
    db?.close();
  }
}

export async function deleteCreatorOptionsCache(key: string): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDatabase();
    if (db) {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).delete(key);
      await finishTransaction(transaction);
    }
  } catch {
    // 削除に失敗してもreset通知を優先する。
  } finally {
    db?.close();
    broadcastCreatorCacheMessage({ type: 'reset', key });
  }
}

/**
 * 週報の保存直後に、発行者候補のキャッシュを全て破棄する。
 * 次回の表示で候補を全件取り直すため、保存した週報の作成者が確実に反映される。
 */
export async function clearAllCreatorOptionsCache(): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDatabase();
    if (!db) return;
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).clear();
    await finishTransaction(transaction);
  } catch {
    // 破棄に失敗しても保存処理は継続する。差分同期が次回の再検証で補う。
  } finally {
    db?.close();
  }
}

export function subscribeCreatorOptionsCacheUpdates(
  key: string,
  onMessage: (message: CreatorCacheBroadcastMessage) => void
): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  try {
    const channel = new BroadcastChannel(CREATOR_CACHE_CHANNEL);
    channel.onmessage = (event: MessageEvent<CreatorCacheBroadcastMessage>) => {
      const message = event.data;
      if (
        message &&
        message.senderId !== CREATOR_CACHE_SENDER_ID &&
        message.key === key &&
        (message.type === 'updated' || message.type === 'reset')
      ) {
        onMessage(message);
      }
    };
    return () => channel.close();
  } catch {
    return () => undefined;
  }
}

export async function withCreatorOptionsFetchLock<T>(
  key: string,
  signal: AbortSignal,
  task: () => Promise<T>
): Promise<T> {
  const lockManager =
    typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!lockManager?.request) return task();
  let taskStarted = false;
  try {
    return await lockManager.request(
      `weekly-report-creators:${key}`,
      { mode: 'exclusive', signal },
      () => {
        taskStarted = true;
        return task();
      }
    );
  } catch (error) {
    const abortLike =
      signal.aborted ||
      (error as any)?.name === 'AbortError' ||
      (error as any)?.code === 'ERR_CANCELED';
    if (taskStarted || abortLike) throw error;
    // 権限・ブラウザ実装差でWeb Locksを利用できない場合は従来動作へ戻す。
    return task();
  }
}

export function mergeCreatorOptionDelta<T extends CachedCreatorOption>(
  current: T[],
  upserts: T[],
  deletedIds: string[]
): T[] {
  const normalizeId = (value: unknown) => String(value ?? '').trim().toLowerCase();
  const merged = new Map<string, T>();

  current.forEach((option) => {
    const key = normalizeId(option.id);
    if (key) merged.set(key, option);
  });
  deletedIds.forEach((id) => merged.delete(normalizeId(id)));
  upserts.forEach((option) => {
    const key = normalizeId(option.id);
    if (key) merged.set(key, option);
  });

  return Array.from(merged.values());
}

export function isCreatorOptionsCacheComplete(
  record: CreatorOptionsCacheRecord
): boolean {
  return record.complete !== false;
}

type CreatorPageResult<T extends CachedCreatorOption> = {
  items: T[];
  nextPageCursor: string | null;
  hasMore: boolean;
  syncCursor: number | null;
};

const waitForBrowserPaint = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
      return;
    }
    setTimeout(resolve, 0);
  });

/**
 * 100件を先行表示し、その後は1,000件ずつ直列取得する。
 * v2レコードは途中カーソルも保持するため、画面を離れても続きから再開できる。
 */
export async function synchronizeCreatorOptionPages<T extends CachedCreatorOption>(args: {
  key: string;
  criteria?: CreatorOptionsCacheCriteria;
  memoryRecord?: CreatorOptionsCacheRecord | null;
  fallbackRecord?: CreatorOptionsCacheRecord | null;
  shouldRevalidate: (record: CreatorOptionsCacheRecord) => boolean;
  fetchPage: (pageCursor: string | null, pageSize: number) => Promise<CreatorPageResult<T>>;
  fetchDelta: (cursor: number) => Promise<CreatorDeltaResult<T>>;
  onRecord: (record: CreatorOptionsCacheRecord) => void;
  onInitialRecord?: (
    record: CreatorOptionsCacheRecord,
    source: 'cache' | 'network'
  ) => void;
  onResetRequired?: () => void;
}): Promise<CreatorOptionsCacheRecord> {
  const indexedRecord = await readCreatorOptionsCache(args.key);
  const candidates = [args.memoryRecord, indexedRecord, args.fallbackRecord].filter(
    (record): record is CreatorOptionsCacheRecord =>
      Boolean(record) &&
      !isCreatorOptionsCacheExpired(record as CreatorOptionsCacheRecord) &&
      (!args.criteria ||
        areCreatorOptionsCacheCriteriaEqual(
          (record as CreatorOptionsCacheRecord).criteria,
          args.criteria
        ))
  );
  const found = candidates.sort((a, b) => b.savedAt - a.savedAt)[0] ?? null;
  // limited=true の旧版は次カーソルを破棄して「完了」と保存していたため、
  // 続きから再開せず先頭100件から全件を取り直す。
  const usableFound = found?.limited === true ? null : found;
  let record: CreatorOptionsCacheRecord | null = usableFound
    ? {
        ...usableFound,
        key: args.key,
        version: 3,
        criteria: args.criteria ?? usableFound.criteria,
        lastAccessedAt: Date.now(),
        expiresAt: Date.now() + CREATOR_CACHE_TTL_MS,
        complete: isCreatorOptionsCacheComplete(usableFound),
        nextPageCursor: usableFound.nextPageCursor ?? null,
      }
    : null;

  if (record) {
    args.onInitialRecord?.({
      ...record,
      options: record.options.slice(0, CREATOR_INITIAL_PAGE_SIZE),
    }, 'cache');
    await waitForBrowserPaint();
    args.onRecord(record);

    if (record.complete && !args.shouldRevalidate(record)) {
      return record;
    }

    if (record.complete && record.cursor != null) {
      const delta = await args.fetchDelta(record.cursor);
      if (!delta.resetRequired) {
        record = {
          ...record,
          version: 3,
          savedAt: Date.now(),
          lastAccessedAt: Date.now(),
          expiresAt: Date.now() + CREATOR_CACHE_TTL_MS,
          cursor: delta.nextCursor,
          complete: true,
          nextPageCursor: null,
          options: mergeCreatorOptionDelta(record.options, delta.upserts, delta.deletedIds),
        };
        args.onRecord(record);
        await writeCreatorOptionsCache(record);
        return record;
      }
      await deleteCreatorOptionsCache(args.key);
      args.onResetRequired?.();
      record = null;
    } else if (record.complete) {
      // 同期カーソルが無い場合は先頭100件から作り直す。
      record = null;
    }
  }

  let resetRefetchCount = 0;
  while (true) {
    let nextPageCursor = record?.nextPageCursor ?? null;
    let options = record?.options ?? [];
    let syncCursor = record?.cursor ?? null;
    let isFirstRequest = record == null;
    let nextPersistAt = Math.max(
      1000,
      Math.ceil((options.length + 1) / 1000) * 1000
    );

    while (true) {
      const page = await args.fetchPage(
        nextPageCursor,
        isFirstRequest ? CREATOR_INITIAL_PAGE_SIZE : CREATOR_BACKGROUND_PAGE_SIZE
      );
      options = mergeCreatorOptionDelta(options as T[], page.items, []) as T[];
      // 全ページ取得中の変更を最後の差分同期で拾えるよう、先頭ページの
      // 同期カーソルを保持する。部分キャッシュ再開時は保存済み値を優先する。
      syncCursor = syncCursor ?? page.syncCursor;
      const complete = !page.hasMore || !page.nextPageCursor;
      nextPageCursor = complete ? null : page.nextPageCursor;
      record = {
        key: args.key,
        version: 3,
        savedAt: Date.now(),
        lastAccessedAt: Date.now(),
        expiresAt: Date.now() + CREATOR_CACHE_TTL_MS,
        criteria: args.criteria,
        cursor: syncCursor,
        options,
        nextPageCursor,
        complete,
      };

      if (isFirstRequest) {
        args.onInitialRecord?.(record, 'network');
        await writeCreatorOptionsCache(record);
        await waitForBrowserPaint();
      }
      args.onRecord(record);

      if (complete) {
        await writeCreatorOptionsCache(record);
        break;
      }
      if (options.length >= nextPersistAt) {
        await writeCreatorOptionsCache(record);
        nextPersistAt += 1000;
      }
      isFirstRequest = false;
    }

    if (record.cursor == null) return record;

    const delta = await args.fetchDelta(record.cursor);
    if (!delta.resetRequired) {
      record = {
        ...record,
        savedAt: Date.now(),
        lastAccessedAt: Date.now(),
        expiresAt: Date.now() + CREATOR_CACHE_TTL_MS,
        cursor: delta.nextCursor,
        options: mergeCreatorOptionDelta(
          record.options as T[],
          delta.upserts,
          delta.deletedIds
        ),
      };
      args.onRecord(record);
      await writeCreatorOptionsCache(record);
      return record;
    }

    // 公開設定など全候補へ影響する変更が取得中に発生した場合は、古い
    // スナップショットを確定せず、最新条件でもう一度先頭から取得する。
    if (resetRefetchCount >= 1) {
      throw new Error('発行者候補の取得中に公開範囲が連続して変更されました');
    }
    await deleteCreatorOptionsCache(args.key);
    args.onResetRequired?.();
    resetRefetchCount += 1;
    record = null;
  }
}

type CreatorSnapshotResult<T extends CachedCreatorOption> = {
  options: T[];
  cursor: number | null;
};

type CreatorDeltaResult<T extends CachedCreatorOption> = {
  nextCursor: number;
  resetRequired: boolean;
  upserts: T[];
  deletedIds: string[];
};

export async function synchronizeCreatorOptions<T extends CachedCreatorOption>(args: {
  key: string;
  memoryRecord?: CreatorOptionsCacheRecord | null;
  fallbackRecord?: CreatorOptionsCacheRecord | null;
  shouldRevalidate: (record: CreatorOptionsCacheRecord) => boolean;
  fetchPreviewSnapshot?: () => Promise<CreatorSnapshotResult<T>>;
  fetchSnapshot: () => Promise<CreatorSnapshotResult<T>>;
  fetchDelta: (cursor: number) => Promise<CreatorDeltaResult<T>>;
  onCachedRecord: (record: CreatorOptionsCacheRecord) => void;
  onPreviewRecord?: (record: CreatorOptionsCacheRecord) => void;
}): Promise<CreatorOptionsCacheRecord> {
  const indexedRecord = await readCreatorOptionsCache(args.key);
  const candidates = [args.memoryRecord, indexedRecord, args.fallbackRecord].filter(
    (record): record is CreatorOptionsCacheRecord => Boolean(record)
  );
  const cached = candidates.sort((a, b) => b.savedAt - a.savedAt)[0] ?? null;

  if (cached) {
    if (cached.options.length > 100 && args.onPreviewRecord) {
      args.onPreviewRecord({
        ...cached,
        options: cached.options.slice(0, 100),
      });
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
    args.onCachedRecord(cached);
    if (!args.shouldRevalidate(cached)) return cached;
  }

  // With no cache, paint a small server-side preview first. It is deliberately
  // not persisted because it is only a partial snapshot.
  if (!cached && args.fetchPreviewSnapshot) {
    const preview = await args.fetchPreviewSnapshot();
    args.onPreviewRecord?.({
      key: args.key,
      version: 1,
      savedAt: Date.now(),
      cursor: preview.cursor,
      options: preview.options,
    });
    // Give React/browser one task boundary to paint the preview before the
    // complete request and its larger JSON processing begin.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  let nextRecord: CreatorOptionsCacheRecord;
  if (cached?.cursor != null) {
    const delta = await args.fetchDelta(cached.cursor);
    if (!delta.resetRequired) {
      nextRecord = {
        key: args.key,
        version: 1,
        savedAt: Date.now(),
        cursor: delta.nextCursor,
        options: mergeCreatorOptionDelta(
          cached.options,
          delta.upserts,
          delta.deletedIds
        ),
      };
      args.onCachedRecord(nextRecord);
      await writeCreatorOptionsCache(nextRecord);
      return nextRecord;
    }
  }

  const snapshot = await args.fetchSnapshot();
  nextRecord = {
    key: args.key,
    version: 1,
    savedAt: Date.now(),
    cursor: snapshot.cursor,
    options: snapshot.options,
  };
  args.onCachedRecord(nextRecord);
  await writeCreatorOptionsCache(nextRecord);
  return nextRecord;
}
